import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Box,
  Card,
  CardContent,
  Chip,
  Collapse,
  IconButton,
  Link as MuiLink,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { Link } from "react-router-dom";
import { getJson } from "../../../utils/api";
import {
  Curriculum,
  AdminGlobalMetrics,
  AdminLessonMetricItem,
  AdminMissedQuestionItem,
} from "../../../models";
import { ADMIN, formatSeconds, fmtPct, fmtNum } from "../utils";

type SortKey = keyof AdminLessonMetricItem;
type SortDir = "asc" | "desc";

const SortableCell: React.FC<{
  label: string;
  sortKey: SortKey;
  activeSortKey: SortKey;
  direction: SortDir;
  onSort: (key: SortKey) => void;
  align?: "left" | "right" | "center";
  tooltip?: string;
}> = ({
  label,
  sortKey: key,
  activeSortKey,
  direction,
  onSort,
  align,
  tooltip,
}) => (
  <TableCell align={align}>
    <Tooltip
      title={tooltip ?? ""}
      arrow
      placement="top"
      disableHoverListener={!tooltip}
    >
      <TableSortLabel
        active={activeSortKey === key}
        direction={activeSortKey === key ? direction : "asc"}
        onClick={() => onSort(key)}
      >
        {label}
      </TableSortLabel>
    </Tooltip>
  </TableCell>
);

const SummaryCards: React.FC<{ metrics: AdminGlobalMetrics }> = ({
  metrics,
}) => {
  const cards = [
    {
      label: "Total Learners",
      value: String(metrics.total_active_learners),
      tooltip: "Unique users who started or completed at least one lesson",
    },
    {
      label: "Lesson Completions",
      value: String(metrics.total_lesson_completions),
      tooltip:
        "Total number of lesson completions across all users (one user completing the same lesson twice counts as two)",
    },
    {
      label: "Curriculum Completions",
      value: String(metrics.total_curriculum_completions),
      tooltip: "Users who completed all lessons in the curriculum",
    },
    {
      label: "Avg Accuracy",
      value: fmtPct(metrics.average_first_attempt_accuracy),
      tooltip: "Average first-attempt accuracy across all completed lessons",
    },
    {
      label: "Avg Time/Lesson",
      value: formatSeconds(metrics.average_time_per_lesson_seconds),
      tooltip: "Average total time spent per completed lesson",
    },
    {
      label: "DAU",
      value: String(metrics.dau),
      tooltip:
        "Daily Active Users — unique users who completed a lesson today (UTC)",
    },
    {
      label: "WAU",
      value: String(metrics.wau),
      tooltip:
        "Weekly Active Users — unique users who completed a lesson in the last 7 days",
    },
  ];

  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mb: 3 }}>
      {cards.map((card) => (
        <Tooltip key={card.label} title={card.tooltip} arrow placement="top">
          <Card sx={{ minWidth: 160, flex: "1 1 0" }}>
            <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
              <Typography variant="caption" color="text.secondary">
                {card.label}
              </Typography>
              <Typography variant="h4">{card.value}</Typography>
            </CardContent>
          </Card>
        </Tooltip>
      ))}
    </Box>
  );
};

const MissedQuestionsTable: React.FC<{
  questions: AdminMissedQuestionItem[];
  showLesson: boolean;
}> = ({ questions, showLesson }) => {
  const colCount = showLesson ? 8 : 6;
  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>Question</TableCell>
          {showLesson && <TableCell>Lesson</TableCell>}
          {showLesson && <TableCell>Unit</TableCell>}
          <TableCell>Type</TableCell>
          <TableCell align="right">First Attempts</TableCell>
          <TableCell align="right">Incorrect</TableCell>
          <TableCell align="right">Miss Rate</TableCell>
          {showLesson && <TableCell align="center">CMS</TableCell>}
        </TableRow>
      </TableHead>
      <TableBody>
        {questions.map((q, idx) => (
          <TableRow key={`${q.lesson_id}-${q.node_id}-${idx}`}>
            <TableCell sx={{ maxWidth: 400 }}>{q.question_text}</TableCell>
            {showLesson && (
              <TableCell>
                {q.lesson_id ? (
                  <MuiLink
                    component={Link}
                    to={`/lessons/${q.lesson_id}/edit`}
                    underline="hover"
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 0.5,
                    }}
                  >
                    {q.lesson_title}
                    <OpenInNewIcon sx={{ fontSize: 14 }} />
                  </MuiLink>
                ) : (
                  q.lesson_title
                )}
              </TableCell>
            )}
            {showLesson && (
              <TableCell>
                {q.curriculum_id ? (
                  <MuiLink
                    component={Link}
                    to={`/curricula/${q.curriculum_id}`}
                    underline="hover"
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 0.5,
                    }}
                  >
                    {q.unit_title ?? "--"}
                    <OpenInNewIcon sx={{ fontSize: 14 }} />
                  </MuiLink>
                ) : (
                  q.unit_title ?? "--"
                )}
              </TableCell>
            )}
            <TableCell>
              <Chip label={q.question_type} variant="outlined" size="small" />
            </TableCell>
            <TableCell align="right">{q.total_first_attempts}</TableCell>
            <TableCell align="right">{q.incorrect_first_attempts}</TableCell>
            <TableCell align="right">{q.miss_rate.toFixed(1)}%</TableCell>
            {showLesson && (
              <TableCell align="center">
                <MuiLink
                  component={Link}
                  to={`/lessons/${q.lesson_id}/edit`}
                  underline="hover"
                >
                  <OpenInNewIcon sx={{ fontSize: 16 }} />
                </MuiLink>
              </TableCell>
            )}
          </TableRow>
        ))}
        {questions.length === 0 && (
          <TableRow>
            <TableCell colSpan={colCount} align="center">
              <Typography variant="body2" color="text.secondary">
                No data
              </Typography>
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  );
};

const OverviewTab: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [globalMetrics, setGlobalMetrics] = useState<AdminGlobalMetrics | null>(
    null
  );
  const [lessonMetrics, setLessonMetrics] = useState<AdminLessonMetricItem[]>(
    []
  );
  const [expandedLessonId, setExpandedLessonId] = useState<string | null>(null);
  const [lessonMissedQuestions, setLessonMissedQuestions] = useState<
    Record<string, AdminMissedQuestionItem[]>
  >({});

  // Curriculum selector
  const [curricula, setCurricula] = useState<Curriculum[]>([]);
  const [selectedCurriculumId, setSelectedCurriculumId] = useState<string>("");

  // Client-side sort
  const [sortKey, setSortKey] = useState<SortKey>("order_index");
  const [sortDir, setSortDir] = useState<SortDir>("asc");

  // Client-side filters
  const [filterUnit, setFilterUnit] = useState<string>("");
  const [filterType, setFilterType] = useState<string>("");

  // Client-side pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // Fix #13: Combine initial data fetches into a single useEffect with Promise.all
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [curricResp, globalResp] = await Promise.all([
          getJson(`${ADMIN}/curricula`),
          getJson(`${ADMIN}/learning/metrics/global`),
        ]);
        if (cancelled) return;
        const curricData = await curricResp.json();
        const globalData = await globalResp.json();
        const list: Curriculum[] = curricData.data ?? [];
        setCurricula(list);
        if (list.length > 0) {
          setSelectedCurriculumId(list[0].id);
        }
        setGlobalMetrics(globalData.data ?? null);
      } catch (e) {
        if (!cancelled) {
          console.error("Failed to fetch initial data", e);
          setError("Failed to load dashboard data");
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Fetch lesson metrics when curriculum changes
  const fetchLessonMetrics = useCallback(async (curriculumId: string) => {
    try {
      setLoading(true);
      setError(null);
      const resp = await getJson(
        `${ADMIN}/learning/metrics/${curriculumId}/lessons`
      );
      const { data } = await resp.json();
      setLessonMetrics(data?.lessons ?? []);
      setPage(0);
      setExpandedLessonId(null);
    } catch (e) {
      console.error("Failed to fetch lesson metrics", e);
      setError("Failed to load lesson metrics");
    } finally {
      setLoading(false);
    }
  }, []);

  // Fix #12: proper dependency array
  useEffect(() => {
    if (!selectedCurriculumId) return;
    fetchLessonMetrics(selectedCurriculumId);
  }, [selectedCurriculumId, fetchLessonMetrics]);

  // Derive unique unit/type values for filter dropdowns
  const unitOptions = useMemo(
    () => Array.from(new Set(lessonMetrics.map((l) => l.unit_title))).sort(),
    [lessonMetrics]
  );
  const typeOptions = useMemo(
    () => Array.from(new Set(lessonMetrics.map((l) => l.lesson_type))).sort(),
    [lessonMetrics]
  );

  // Filtered + sorted data
  const processedLessons = useMemo(() => {
    let filtered = lessonMetrics;
    if (filterUnit) {
      filtered = filtered.filter((l) => l.unit_title === filterUnit);
    }
    if (filterType) {
      filtered = filtered.filter((l) => l.lesson_type === filterType);
    }

    const sorted = [...filtered].sort((a, b) => {
      const aVal = a[sortKey] ?? 0;
      const bVal = b[sortKey] ?? 0;
      if (aVal < bVal) return sortDir === "asc" ? -1 : 1;
      if (aVal > bVal) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [lessonMetrics, filterUnit, filterType, sortKey, sortDir]);

  // Paginated slice
  const paginatedLessons = useMemo(
    () => processedLessons.slice(page * rowsPerPage, (page + 1) * rowsPerPage),
    [processedLessons, page, rowsPerPage]
  );

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const fetchLessonMissed = useCallback(async (lessonId: string) => {
    try {
      const resp = await getJson(
        `${ADMIN}/learning/metrics/missed-questions?lesson_id=${lessonId}&limit=50`
      );
      const { data } = await resp.json();
      setLessonMissedQuestions((prev) => ({
        ...prev,
        [lessonId]: data?.questions ?? [],
      }));
    } catch (e) {
      console.error("Failed to fetch missed questions for lesson", e);
    }
  }, []);

  const handleToggleLesson = (lessonId: string) => {
    if (expandedLessonId === lessonId) {
      setExpandedLessonId(null);
    } else {
      setExpandedLessonId(lessonId);
      if (!lessonMissedQuestions[lessonId]) {
        fetchLessonMissed(lessonId);
      }
    }
  };

  if (error) return <Typography color="error">{error}</Typography>;

  return (
    <>
      {globalMetrics && <SummaryCards metrics={globalMetrics} />}

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          mb: 1,
          mt: 3,
        }}
      >
        <Typography variant="h6">Per-Lesson Metrics</Typography>
        {curricula.length > 0 && (
          <TextField
            select
            size="small"
            label="Curriculum"
            value={selectedCurriculumId}
            onChange={(e) => setSelectedCurriculumId(e.target.value)}
            sx={{ minWidth: 200 }}
          >
            {curricula.map((c) => (
              <MenuItem key={c.id} value={c.id}>
                {c.title}
              </MenuItem>
            ))}
          </TextField>
        )}
        {unitOptions.length > 1 && (
          <TextField
            select
            size="small"
            label="Unit"
            value={filterUnit}
            onChange={(e) => {
              setFilterUnit(e.target.value);
              setPage(0);
            }}
            sx={{ minWidth: 160 }}
          >
            <MenuItem value="">All Units</MenuItem>
            {unitOptions.map((u) => (
              <MenuItem key={u} value={u}>
                {u}
              </MenuItem>
            ))}
          </TextField>
        )}
        {typeOptions.length > 1 && (
          <TextField
            select
            size="small"
            label="Type"
            value={filterType}
            onChange={(e) => {
              setFilterType(e.target.value);
              setPage(0);
            }}
            sx={{ minWidth: 140 }}
          >
            <MenuItem value="">All Types</MenuItem>
            {typeOptions.map((t) => (
              <MenuItem key={t} value={t}>
                {t}
              </MenuItem>
            ))}
          </TextField>
        )}
      </Box>

      {loading ? (
        <Typography>Loading...</Typography>
      ) : (
        <TableContainer component={Paper} sx={{ mb: 3 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell />
                <SortableCell
                  label="Lesson"
                  sortKey="lesson_title"
                  activeSortKey={sortKey}
                  direction={sortDir}
                  onSort={handleSort}
                />
                <SortableCell
                  label="Unit"
                  sortKey="unit_title"
                  activeSortKey={sortKey}
                  direction={sortDir}
                  onSort={handleSort}
                />
                <TableCell>Type</TableCell>
                <SortableCell
                  label="Started"
                  sortKey="started_count"
                  activeSortKey={sortKey}
                  direction={sortDir}
                  onSort={handleSort}
                  align="right"
                  tooltip="Users who started this lesson (in progress + completed)"
                />
                <SortableCell
                  label="Completed"
                  sortKey="completed_count"
                  activeSortKey={sortKey}
                  direction={sortDir}
                  onSort={handleSort}
                  align="right"
                  tooltip="Users who completed this lesson"
                />
                <SortableCell
                  label="Rate"
                  sortKey="completion_rate"
                  activeSortKey={sortKey}
                  direction={sortDir}
                  onSort={handleSort}
                  align="right"
                  tooltip="Completed / Started"
                />
                <SortableCell
                  label="Accuracy"
                  sortKey="average_accuracy"
                  activeSortKey={sortKey}
                  direction={sortDir}
                  onSort={handleSort}
                  align="right"
                  tooltip="Average first-attempt accuracy among users who completed this lesson"
                />
                <SortableCell
                  label="Avg Time"
                  sortKey="average_time_seconds"
                  activeSortKey={sortKey}
                  direction={sortDir}
                  onSort={handleSort}
                  align="right"
                  tooltip="Average total time spent by users who completed this lesson"
                />
                <SortableCell
                  label="Retries"
                  sortKey="average_retry_count"
                  activeSortKey={sortKey}
                  direction={sortDir}
                  onSort={handleSort}
                  align="right"
                  tooltip="Average number of retry attempts per completed lesson"
                />
                <SortableCell
                  label="Drop-off"
                  sortKey="drop_off_rate"
                  activeSortKey={sortKey}
                  direction={sortDir}
                  onSort={handleSort}
                  align="right"
                  tooltip="Percentage of users who started but did not complete: (Started - Completed) / Started"
                />
                <TableCell align="center">CMS</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedLessons.map((lesson) => (
                <React.Fragment key={lesson.lesson_id}>
                  <TableRow
                    hover
                    onClick={() => handleToggleLesson(lesson.lesson_id)}
                    sx={{ cursor: "pointer" }}
                  >
                    <TableCell sx={{ width: 32, px: 0.5 }}>
                      <IconButton size="small">
                        {expandedLessonId === lesson.lesson_id ? (
                          <ExpandLessIcon fontSize="small" />
                        ) : (
                          <ExpandMoreIcon fontSize="small" />
                        )}
                      </IconButton>
                    </TableCell>
                    <TableCell>{lesson.lesson_title}</TableCell>
                    <TableCell>{lesson.unit_title}</TableCell>
                    <TableCell>
                      <Chip
                        label={lesson.lesson_type}
                        variant="outlined"
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="right">{lesson.started_count}</TableCell>
                    <TableCell align="right">
                      {lesson.completed_count}
                    </TableCell>
                    <TableCell align="right">
                      {fmtPct(lesson.completion_rate)}
                    </TableCell>
                    <TableCell align="right">
                      {fmtPct(lesson.average_accuracy)}
                    </TableCell>
                    <TableCell align="right">
                      {formatSeconds(lesson.average_time_seconds)}
                    </TableCell>
                    <TableCell align="right">
                      {fmtNum(lesson.average_retry_count)}
                    </TableCell>
                    <TableCell align="right">
                      {fmtPct(lesson.drop_off_rate)}
                    </TableCell>
                    <TableCell
                      align="center"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <MuiLink
                        component={Link}
                        to={`/lessons/${lesson.lesson_id}/edit`}
                        underline="hover"
                      >
                        <OpenInNewIcon sx={{ fontSize: 16 }} />
                      </MuiLink>
                    </TableCell>
                  </TableRow>

                  <TableRow>
                    <TableCell
                      colSpan={12}
                      sx={{ py: 0, borderBottom: "none" }}
                    >
                      <Collapse
                        in={expandedLessonId === lesson.lesson_id}
                        timeout="auto"
                        unmountOnExit
                      >
                        <Box sx={{ py: 1, px: 2 }}>
                          <Typography variant="subtitle2" sx={{ mb: 1 }}>
                            Question Stats
                          </Typography>
                          {lessonMissedQuestions[lesson.lesson_id] ? (
                            <MissedQuestionsTable
                              questions={[
                                ...lessonMissedQuestions[lesson.lesson_id],
                              ].sort((a, b) => a.order_index - b.order_index)}
                              showLesson={false}
                            />
                          ) : (
                            <Typography variant="body2">Loading...</Typography>
                          )}
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))}
              {paginatedLessons.length === 0 && (
                <TableRow>
                  <TableCell colSpan={12} align="center">
                    <Typography variant="body2" color="text.secondary">
                      No data
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          {processedLessons.length > 10 && (
            <TablePagination
              component="div"
              count={processedLessons.length}
              page={page}
              onPageChange={(_, p) => setPage(p)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[10, 25, 50]}
            />
          )}
        </TableContainer>
      )}
    </>
  );
};

export default React.memo(OverviewTab);
